import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mail, MessageCircle, HelpCircle, Package, CreditCard, Truck, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupportPage = () => {
  const [chatMessages, setChatMessages] = useState([
    { from: 'bot', text: 'Ciao! Sono l\'assistente virtuale di QANTRI. Come posso aiutarti oggi?' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSend = async () => {
    if (!chatInput.trim() || isTyping) return;
    
    const userMessage = chatInput.trim();
    setChatMessages(prev => [...prev, { from: 'user', text: userMessage }]);
    setChatInput('');
    setIsTyping(true);
    
    try {
      const response = await axios.post(`${API}/chat?message=${encodeURIComponent(userMessage)}`, {}, {
        timeout: 10000 // 10 second timeout
      });
      setChatMessages(prev => [...prev, { from: 'bot', text: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { from: 'bot', text: 'Mi scusi, si è verificato un errore. Riprova o contattaci a postmaster@qantri.store' }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickQuestions = [
    { icon: Truck, text: 'Tempi di spedizione' },
    { icon: RotateCcw, text: 'Come fare un reso' },
    { icon: CreditCard, text: 'Metodi di pagamento' },
    { icon: Package, text: 'Traccia il mio ordine' },
  ];

  const handleQuickQuestion = (question) => {
    setChatInput(question);
    // Auto-send after a short delay
    setTimeout(() => {
      if (question) {
        handleSendQuick(question);
      }
    }, 100);
  };

  const handleSendQuick = async (message) => {
    if (!message.trim() || isTyping) return;
    
    setChatMessages(prev => [...prev, { from: 'user', text: message }]);
    setChatInput('');
    setIsTyping(true);
    
    try {
      const response = await axios.post(`${API}/chat?message=${encodeURIComponent(message)}`, {}, {
        timeout: 10000
      });
      setChatMessages(prev => [...prev, { from: 'bot', text: response.data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { from: 'bot', text: 'Errore. Riprova o contattaci a postmaster@qantri.store' }]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-12 md:py-20" data-testid="support-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-4 block">
            Assistenza Clienti
          </span>
          <h1 className="text-4xl md:text-5xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Come possiamo <span className="italic">aiutarti</span>?
          </h1>
          <p className="text-neutral-500 font-light max-w-xl mx-auto">
            Il nostro assistente virtuale è disponibile 24/7. Per assistenza personalizzata, scrivici a postmaster@qantri.store
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* FAQ Quick Access */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-1 space-y-6"
          >
            <div className="bg-white rounded-2xl border border-neutral-200 p-6">
              <h3 className="font-medium mb-4 flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-[#064E3B]" />
                Domande Frequenti
              </h3>
              <div className="space-y-3">
                {quickQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleQuickQuestion(q.text)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-neutral-50 hover:bg-neutral-100 transition-colors text-left text-sm"
                  >
                    <q.icon className="h-4 w-4 text-neutral-400" />
                    <span>{q.text}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-[#064E3B] rounded-2xl p-6 text-white">
              <h3 className="font-medium mb-4">Contatti Diretti</h3>
              <div className="space-y-4">
                <a 
                  href="mailto:postmaster@qantri.store" 
                  className="flex items-center gap-3 text-white/80 hover:text-white transition-colors text-sm"
                >
                  <Mail className="h-4 w-4" />
                  postmaster@qantri.store
                </a>
              </div>
              <p className="text-white/50 text-xs mt-4">
                Rispondiamo entro 24 ore lavorative
              </p>
            </div>
          </motion.div>

          {/* Chat Window */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden flex flex-col" style={{ height: '600px' }}>
              {/* Chat Header */}
              <div className="flex items-center gap-3 p-5 border-b border-neutral-100 bg-gradient-to-r from-[#064E3B] to-[#0A6B4F] text-white">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-medium">Assistente QANTRI</h3>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    <span className="text-xs text-white/70">Online</span>
                  </div>
                </div>
              </div>
              
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-neutral-50">
                {chatMessages.map((msg, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] p-4 text-sm rounded-2xl ${
                      msg.from === 'user' 
                        ? 'bg-[#064E3B] text-white rounded-br-sm' 
                        : 'bg-white text-neutral-800 border border-neutral-100 rounded-bl-sm shadow-sm'
                    }`}>
                      {msg.text}
                    </div>
                  </motion.div>
                ))}
                
                {/* Typing Indicator */}
                <AnimatePresence>
                  {isTyping && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex justify-start"
                    >
                      <div className="bg-white border border-neutral-100 rounded-2xl rounded-bl-sm p-4 shadow-sm">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-neutral-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-neutral-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-neutral-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <div ref={chatEndRef} />
              </div>
              
              {/* Chat Input */}
              <div className="p-4 border-t border-neutral-100 bg-white">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Scrivi un messaggio..."
                    className="flex-1 px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#064E3B]/20 focus:border-[#064E3B] transition-all"
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={!chatInput.trim()}
                    className="px-6 bg-[#064E3B] hover:bg-[#043927] text-white rounded-xl disabled:opacity-50"
                    data-testid="chat-send-btn"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
